import { Block, Card, Checkbox, Flex, Paginator, Text } from '@upsales/components';
import { CompanyRowBranchOffices } from './CompanyRow';
import ProspectingClient from 'App/resources/Model/ProspectingClient';
import SoliditetClient from 'App/resources/Model/SoliditetClient';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useState, useMemo } from 'react';

import './BranchOffices.scss';
import { useAddDispatch, useAddSelector } from './Add/context/AddContext';
import { setSelectedBranches } from './Add/context/Helpers';

const RESULT_LIMIT = 20;

type BranchOfficesProps = {
	hasPendingPurchase: boolean;
	unknowns: ProspectingClient[] | SoliditetClient[];
	onChange: (
		branches: ProspectingClient[] | SoliditetClient[],
		filteredBranches?: ProspectingClient[] | SoliditetClient[]
	) => void;
	deselectAll?: () => void;
};

const BranchOffices = ({ hasPendingPurchase, unknowns, onChange, deselectAll }: BranchOfficesProps) => {
	const classes = new BemClass('BranchOffices');
	const { t } = useTranslation();
	const dispatch = useAddDispatch();

	const {
		activeTab,
		selectedBranches,
		searchQuery = '',
		isProspecting,
		selectedClients
	} = useAddSelector(state => state);

	const [branchOfficesOffset, setBranchOfficesOffset] = useState(0);
	const [branchOffices, setBranchOffices] = useState<ProspectingClient[] | SoliditetClient[]>(unknowns || []);

	const isSameBranch = (branchA: ProspectingClient | SoliditetClient, branchB: ProspectingClient | SoliditetClient) =>
		isProspecting
			? (branchA as ProspectingClient).prospectingId === (branchB as ProspectingClient).prospectingId
			: (branchA as SoliditetClient).dunsNo === (branchB as SoliditetClient).dunsNo;

	const getTypedBranches = (branches: (ProspectingClient | SoliditetClient)[]) => {
		if (isProspecting) {
			return branches as ProspectingClient[];
		}
		return branches as SoliditetClient[];
	};

	// Need this to make typescript happy
	const findInBranches = (
		branches: (ProspectingClient | SoliditetClient)[],
		branch: ProspectingClient | SoliditetClient
	) => {
		return branches.find(b => isSameBranch(b, branch));
	};

	// Normal filter transforms the array to (ProspectingClient | SoliditetClient)[] which is not the same as the original array
	const getProperlyTypedFilter = (
		branch: ProspectingClient[] | SoliditetClient[],
		filterFunc: (a: ProspectingClient | SoliditetClient) => boolean
	) => {
		const res = [];
		for (const item of branch) {
			if (filterFunc(item)) {
				res.push(item);
			}
		}
		return getTypedBranches(res);
	};

	const toggleBranch = (branch: ProspectingClient | SoliditetClient) => {
		const prevSelected = findInBranches(selectedBranches, branch);
		if (prevSelected) {
			const newSelectedBranches = getProperlyTypedFilter(
				selectedBranches,
				branchItem => !isSameBranch(prevSelected, branchItem)
			);

			dispatch(setSelectedBranches(newSelectedBranches));
		} else {
			const newSelectedBranches = getTypedBranches([...selectedBranches, branch]);

			dispatch(setSelectedBranches(newSelectedBranches));
		}
	};

	// Filter branch offices based on search query
	const filteredBranchOffices = useMemo(() => {
		if (!searchQuery.trim()) {
			return branchOffices;
		}

		return branchOffices.filter(office => office.name?.toLowerCase().includes(searchQuery.toLowerCase()));
	}, [branchOffices, searchQuery]);

	const toggleAll = () => {
		if (activeTab === 'selected' && deselectAll) {
			deselectAll();
			dispatch(setSelectedBranches([]));
			return;
		}

		// Check if all filtered branches are selected
		const allFilteredAreSelected = filteredBranchOffices.every(branch =>
			selectedBranches.some(selected => isSameBranch(selected, branch))
		);

		if (allFilteredAreSelected) {
			// Deselect all filtered branches
			const newSelectedBranches = getProperlyTypedFilter(
				selectedBranches,
				branch => !filteredBranchOffices.some(filtered => isSameBranch(filtered, branch))
			);
			dispatch(setSelectedBranches(newSelectedBranches));
		} else {
			// Select all filtered branches (add missing ones)
			const newSelectedBranches = [...selectedBranches];
			for (const branch of filteredBranchOffices) {
				if (!findInBranches(selectedBranches, branch)) {
					newSelectedBranches.push(branch);
				}
			}
			dispatch(setSelectedBranches(getTypedBranches(newSelectedBranches)));
		}
	};

	useEffect(() => {
		const refreshBranchOffices = () => {
			const availableOffices = getProperlyTypedFilter(
				unknowns ?? [],
				office => !(office as ProspectingClient).matchInUpsales?.operationalAccount && !hasPendingPurchase
			);
			let typedOffices = [];
			if (isProspecting) {
				typedOffices = availableOffices as ProspectingClient[];
			} else {
				typedOffices = availableOffices as SoliditetClient[];
			}
			setBranchOffices(typedOffices || []);
		};
		refreshBranchOffices();
	}, [unknowns, hasPendingPurchase]);

	useEffect(() => {
		const refreshSelected = () => {
			const newSelecteds: ProspectingClient[] = [];
			const toRemove: ProspectingClient[] = [];

			// Prospecting branches can have match in upsales
			for (const client of selectedClients) {
				const selectBranch = (branchOffices as ProspectingClient[]).find(
					branch => branch.matchInUpsales?.id === client.id
				) as ProspectingClient;
				if (
					selectBranch &&
					!(selectedBranches as ProspectingClient[]).find(
						branch => branch.prospectingId === selectBranch.prospectingId
					)
				) {
					newSelecteds.push(selectBranch);
				}
			}
			for (const branch of selectedBranches) {
				const typedBranch = branch as ProspectingClient;
				if (
					typedBranch.matchInUpsales &&
					!selectedClients.find(({ id }) => id === typedBranch.matchInUpsales?.id)
				) {
					toRemove.push(typedBranch);
				}
			}

			const newSelectedBranches = getProperlyTypedFilter(
				selectedBranches,
				branchItem =>
					!(branchItem as ProspectingClient).matchInUpsales ||
					!toRemove.some(e => e.matchInUpsales?.id === (branchItem as ProspectingClient).matchInUpsales?.id)
			);

			dispatch(setSelectedBranches(newSelectedBranches));
		};
		if (isProspecting) {
			refreshSelected();
		}
	}, [selectedClients, isProspecting]);

	useEffect(() => {
		onChange(
			selectedBranches,
			isProspecting
				? (filteredBranchOffices as ProspectingClient[])
				: (filteredBranchOffices as SoliditetClient[])
		);
	}, [selectedBranches, filteredBranchOffices]);

	useEffect(() => {
		setBranchOfficesOffset(0);
	}, [activeTab, filteredBranchOffices]);

	const allFilteredSelected = filteredBranchOffices.every(branch =>
		selectedBranches.some(selected => isSameBranch(selected, branch))
	);
	const someFilteredSelected = filteredBranchOffices.some(branch =>
		selectedBranches.some(selected => isSameBranch(selected, branch))
	);

	return filteredBranchOffices?.length || (activeTab === 'selected' && selectedClients.length) ? (
		<>
			<Block className={classes.b()}>
				<Card space="pll ptm pbm" border="bm" borderColor="grey-4">
					<Flex alignItems="center" space="mls" gap="u4">
						<Checkbox
							size="xs"
							checked={allFilteredSelected}
							half={someFilteredSelected && !allFilteredSelected}
							onClick={toggleAll}
						/>
						<Text size="sm">
							{activeTab === 'selected'
								? t('account.relations.selectedCompaniesToRelate')
								: t('default.selectAll')}
						</Text>
					</Flex>
				</Card>
				{filteredBranchOffices
					.slice(branchOfficesOffset, branchOfficesOffset + RESULT_LIMIT)
					.map(branchOffice => (
						<CompanyRowBranchOffices
							key={
								isProspecting
									? (branchOffice as ProspectingClient).prospectingId
									: (branchOffice as SoliditetClient).dunsNo
							}
							company={branchOffice}
							onSelectCompany={toggleBranch}
							isProspecting={isProspecting}
							selected={selectedBranches.some(branch => isSameBranch(branch, branchOffice))}
						/>
					))}
			</Block>
			{filteredBranchOffices.length > RESULT_LIMIT ? (
				<Block space="mtm mbl">
					<Paginator
						limit={RESULT_LIMIT}
						offset={branchOfficesOffset}
						total={filteredBranchOffices.length}
						onChange={offset => {
							setBranchOfficesOffset(offset);
						}}
					/>
				</Block>
			) : null}
		</>
	) : null;
};

export default BranchOffices;
